export interface CompoundOptions {
  initialAsset: number;
  monthlyContribution: number;
  investFrequency: 1 | 4 | 12; // periods per year (월=12, 분기=4, 연=1)
  period: number;
  annualReturnRate: number;
}

export interface CompoundDataPoint {
  year: number;
  totalAsset: number;
  principal: number;
  interest: number;
}

// Legacy chart components still reference this shape.
export interface CompoundHistory {
  time: Date;
  amount: number;
  principalAmount: number;
  totalInterest: number;
}

// Legacy summary table still imports this historical result shape.
export interface CalculationResult {
  options: {
    initialAmount: number;
    frequentInvestment: number;
    investFrequency: 1 | 4 | 12;
    period: number;
    interestPercentage: number;
  };
  transitions: CompoundHistory[];
}

export interface CompoundResult {
  dataPoints: CompoundDataPoint[];
  finalAsset: number;
  totalPrincipal: number;
  totalInterest: number;
  profitRatePct: number;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('ko-KR').format(value);
}

export function compactFormatNumber(value: number): string {
  return new Intl.NumberFormat('ko-KR', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(value);
}

export function calculateCompound(options: CompoundOptions): CompoundResult {
  const { initialAsset, monthlyContribution, investFrequency, period, annualReturnRate } = options;
  const periodsPerYear = investFrequency;
  const ratePerPeriod = annualReturnRate / 100 / periodsPerYear;
  const totalPeriods = period * periodsPerYear;
  const contributionPerPeriod = monthlyContribution * (12 / periodsPerYear);

  let asset = initialAsset;
  let principal = initialAsset;
  const dataPoints: CompoundDataPoint[] = [
    { year: 0, totalAsset: asset, principal, interest: 0 },
  ];

  for (let p = 1; p <= totalPeriods; p++) {
    asset = asset * (1 + ratePerPeriod) + contributionPerPeriod;
    principal += contributionPerPeriod;
    if (p % periodsPerYear === 0) {
      dataPoints.push({
        year: p / periodsPerYear,
        totalAsset: Math.round(asset),
        principal: Math.round(principal),
        interest: Math.round(asset - principal),
      });
    }
  }

  const finalAsset = Math.round(asset);
  const totalPrincipalFinal = Math.round(principal);
  const totalInterest = finalAsset - totalPrincipalFinal;
  const profitRatePct =
    totalPrincipalFinal > 0 ? Math.round((totalInterest / totalPrincipalFinal) * 100) : 0;

  return { dataPoints, finalAsset, totalPrincipal: totalPrincipalFinal, totalInterest, profitRatePct };
}
